import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { LANGUAGES, type Class, type Session } from "@shared/schema";
import {
  Clock, ChevronRight, Video, Search, Globe, X, Filter,
} from "lucide-react";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import { DEFAULT_THUMBNAIL } from "@/lib/constants";
import { format, isToday, isTomorrow } from "date-fns";

interface DashboardSession extends Session {
  classTitle: string;
  classThumbnail: string | null;
  classCategory: string | null;
  classLanguage: string;
  providerName: string;
}

interface DashboardData {
  live: DashboardSession[];
  upcoming: DashboardSession[];
  classes: Class[];
}

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const searchParams = useSearch();
  const params = new URLSearchParams(searchParams);
  const initialLang = params.get("language") || "";

  const [selectedLanguage, setSelectedLanguage] = useState(initialLang);
  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ["/api/dashboard"],
  });

  const { data: activeLanguages } = useQuery<{ value: string; label: string; flag: string }[]>({
    queryKey: ["/api/languages/active"],
  });

  const live = data?.live || [];
  const upcoming = data?.upcoming || [];
  const classes = data?.classes || [];

  const isFiltering = !!selectedLanguage || !!searchQuery;

  const filteredClasses = useMemo(() => {
    if (!isFiltering) return null;
    return classes.filter((cls) => {
      const matchesLang = !selectedLanguage || cls.language === selectedLanguage;
      const matchesSearch =
        !searchQuery ||
        cls.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cls.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cls.category?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesLang && matchesSearch;
    });
  }, [classes, selectedLanguage, searchQuery, isFiltering]);

  const classesByCategory = useMemo(() => {
    return classes.reduce<Record<string, Class[]>>((acc, cls) => {
      const cat = cls.category || "General";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(cls);
      return acc;
    }, {});
  }, [classes]);

  const classesByLanguage = useMemo(() => {
    return classes.reduce<Record<string, Class[]>>((acc, cls) => {
      if (!acc[cls.language]) acc[cls.language] = [];
      acc[cls.language].push(cls);
      return acc;
    }, {});
  }, [classes]);

  if (isLoading) {
    return (
      <div className="min-h-screen px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-7xl space-y-8">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="aspect-video w-full rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="aspect-video w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="glass-page-bg" />
      {!user && (
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 glass-hero-bg" />
          <div className="absolute inset-0 gradient-mesh" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(59, 130, 246, 0.08) 0%, transparent 60%)" }} />
          <div className="hero-light-sweep" />
          <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.25) 50%, transparent 100%)" }} />
          <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-center gap-5 px-4 py-20 sm:px-6 sm:py-28 md:py-36">
            <Badge variant="outline" className="border-primary/30 text-primary/80 no-default-hover-elevate glass-input shimmer-border" style={{ backdropFilter: "blur(12px)" }}>
              {t("hero.badge", "Live Forex Trading Education")}
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl glow-text" data-testid="text-hero-title">
              {t("hero.title")}
            </h1>
            <p className="max-w-lg text-base text-white/40 sm:text-lg" data-testid="text-hero-subtitle">
              {t("hero.subtitle")}
            </p>
            <div className="flex items-center gap-3">
              <Button
                size="lg"
                onClick={() => navigate("/login")}
                className="btn-gradient-capsule text-base px-8 py-3 btn-cta-glow"
                data-testid="button-join-free"
              >
                {t("hero.cta")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <div className="mb-6 glass-panel-sm depth-shadow-sm p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => { setSelectedLanguage(""); setSearchQuery(""); }}
                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${!selectedLanguage ? "cal-glass-pill cal-glass-pill-active" : "cal-glass-pill text-muted-foreground"}`}
                data-testid="button-filter-all"
              >
                {t("browse.all")}
              </button>
              {(activeLanguages || []).filter((l) => l.value !== "OTHER").map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => setSelectedLanguage(lang.value === selectedLanguage ? "" : lang.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${selectedLanguage === lang.value ? "cal-glass-pill cal-glass-pill-active" : "cal-glass-pill text-muted-foreground"}`}
                  data-testid={`button-filter-${lang.value.toLowerCase()}`}
                >
                  {getFlagEmoji(lang.flag)} {lang.label}
                </button>
              ))}
              {selectedLanguage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedLanguage("")}
                  className="text-muted-foreground"
                  data-testid="button-clear-filter"
                >
                  <X className="mr-1 h-3 w-3" />
                  {t("browse.clear")}
                </Button>
              )}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("browse.searchClasses")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 glass-input"
                data-testid="input-search-classes"
              />
            </div>
          </div>
        </div>

        {isFiltering ? (
          <div className="glass-section p-5 sm:p-6">
            {filteredClasses && filteredClasses.length > 0 ? (
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {filteredClasses.map((cls) => (
                  <ClassCard key={cls.id} cls={cls} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="empty-state-icon mb-4">
                  <Filter className="h-10 w-10 text-primary/25" />
                </div>
                <p className="font-semibold text-muted-foreground">{t("browse.noClasses")}</p>
                <p className="mt-1.5 text-sm text-muted-foreground/50 max-w-xs leading-relaxed">
                  {t("browse.adjustFilters")}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {live.length > 0 && (
              <section className="glass-section depth-shadow-sm p-5 sm:p-6 animate-fade-in-up">
                <SectionHeader
                  title={t("landing.liveNow")}
                  icon={<LiveBroadcastIcon size={18} />}
                  action={() => navigate("/calendar")}
                  data-testid="section-live"
                />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {live.map((s) => (
                    <LiveSessionCard key={s.id} session={s} />
                  ))}
                </div>
              </section>
            )}

            {upcoming.length > 0 && (
              <section className="glass-section p-5 sm:p-6 animate-fade-in-up stagger-2">
                <SectionHeader
                  title={t("landing.upcoming")}
                  icon={<Clock className="h-4 w-4 text-primary" />}
                  action={() => navigate("/calendar")}
                  data-testid="section-upcoming"
                />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {upcoming.slice(0, 8).map((s) => (
                    <SessionCard key={s.id} session={s} />
                  ))}
                </div>
              </section>
            )}

            {Object.entries(classesByCategory).map(([category, catClasses]) => (
              <section key={category} className="glass-section p-5 sm:p-6 animate-fade-in-up stagger-3">
                <SectionHeader title={category} />
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {catClasses.slice(0, 5).map((cls) => (
                    <ClassCard key={cls.id} cls={cls} />
                  ))}
                </div>
              </section>
            ))}

            {Object.keys(classesByLanguage).length > 1 && (
              <>
                {Object.entries(classesByLanguage)
                  .filter(([, langClasses]) => langClasses.length > 0)
                  .slice(0, 3)
                  .map(([lang, langClasses]) => {
                    const langInfo = LANGUAGES.find((l) => l.value === lang);
                    if (!langInfo) return null;
                    return (
                      <section key={lang} className="glass-section p-5 sm:p-6 animate-fade-in-up stagger-4">
                        <SectionHeader
                          title={`${getFlagEmoji(langInfo.flag)} ${langInfo.label}`}
                          action={() => setSelectedLanguage(lang)}
                        />
                        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                          {langClasses.slice(0, 5).map((cls) => (
                            <ClassCard key={cls.id} cls={cls} />
                          ))}
                        </div>
                      </section>
                    );
                  })}
              </>
            )}

            {classes.length === 0 && upcoming.length === 0 && live.length === 0 && (
              <div className="glass-section depth-shadow-md flex flex-col items-center justify-center py-20 text-center">
                <div className="empty-state-icon mb-5">
                  <Video className="h-16 w-16 text-primary/25" />
                </div>
                <p className="text-lg font-semibold text-muted-foreground glow-text-sm">{t("landing.noClasses")}</p>
                <p className="mt-2 text-sm text-muted-foreground/50 max-w-xs leading-relaxed">
                  {t("landing.checkBack")}
                </p>
                <div className="divider-glow w-32 my-5" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  icon,
  action,
  "data-testid": testId,
}: {
  title: string;
  icon?: React.ReactNode;
  action?: () => void;
  "data-testid"?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex items-center justify-between gap-4" data-testid={testId}>
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        {icon}
        {title}
      </h2>
      {action && (
        <Button variant="ghost" size="sm" onClick={action} className="text-muted-foreground">
          {t("landing.seeAll")}
          <ChevronRight className="ml-0.5 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function LiveSessionCard({ session }: { session: DashboardSession }) {
  const { t } = useTranslation();
  const lang = LANGUAGES.find((l) => l.value === session.classLanguage);
  const viewerCount = (session as any).attendanceCount || 0;

  return (
    <Link href={`/live/${session.id}`}>
      <div className="group cursor-pointer" data-testid={`card-live-${session.id}`}>
        <div className="relative overflow-hidden rounded-xl glass-card glass-card-hover">
          <img
            src={session.classThumbnail || DEFAULT_THUMBNAIL}
            alt={session.classTitle}
            className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <Badge variant="destructive" className="absolute left-3 top-3 no-default-hover-elevate" data-testid={`badge-live-${session.id}`} style={{ boxShadow: "0 0 12px rgba(239, 68, 68, 0.4)" }}>
            <LiveBroadcastIcon size={14} />
            LIVE
          </Badge>
          {viewerCount > 0 && (
            <div className="absolute bottom-3 right-3 rounded-md px-2 py-0.5 text-xs text-white/90 glass-card" style={{ fontSize: "11px" }}>
              {t("landing.viewers", { count: viewerCount })}
            </div>
          )}
          <div className="absolute bottom-3 left-3 right-16">
            <p className="text-sm font-semibold text-white line-clamp-1">{session.classTitle}</p>
            <p className="text-xs text-white/50">{session.providerName}</p>
          </div>
        </div>
        <div className="mt-2.5 flex items-start gap-2.5">
          <Avatar className="h-8 w-8 shrink-0 mt-0.5">
            <AvatarFallback className="bg-primary/20 text-primary text-xs">
              {session.providerName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold line-clamp-1">{session.classTitle}</p>
            <p className="text-xs text-muted-foreground">{session.providerName}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {session.classCategory && (
                <Badge variant="secondary" className="text-xs no-default-hover-elevate">
                  {session.classCategory}
                </Badge>
              )}
              {lang && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Globe className="h-3 w-3" />
                  {lang.label}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function SessionCard({ session }: { session: DashboardSession }) {
  const { t } = useTranslation();
  const lang = LANGUAGES.find((l) => l.value === session.classLanguage);
  const startDate = new Date(session.startsAt);
  const timeLabel = isToday(startDate)
    ? t("landing.today", { time: format(startDate, "h:mm a") })
    : isTomorrow(startDate)
      ? t("landing.tomorrow", { time: format(startDate, "h:mm a") })
      : format(startDate, "MMM d, h:mm a");

  return (
    <Link href={`/session/${session.id}`}>
      <div className="group cursor-pointer" data-testid={`card-session-${session.id}`}>
        <div className="relative overflow-hidden rounded-xl glass-card glass-card-hover">
          <img
            src={session.classThumbnail || DEFAULT_THUMBNAIL}
            alt={session.classTitle}
            className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {lang && (
            <span className="absolute right-2.5 top-2.5 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-white/80 glass-card" style={{ fontSize: "11px" }}>
              <Globe className="h-3 w-3" />
              {lang.label}
            </span>
          )}
        </div>
        <div className="mt-2.5 flex items-start gap-2.5">
          <Avatar className="h-8 w-8 shrink-0 mt-0.5">
            <AvatarFallback className="bg-primary/20 text-primary text-xs">
              {session.providerName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium line-clamp-1" data-testid={`text-session-title-${session.id}`}>
              {session.classTitle}
            </p>
            <p className="text-xs text-muted-foreground">{session.providerName}</p>
            <div className="mt-1 flex items-center gap-1 text-xs text-primary">
              <Clock className="h-3 w-3" />
              {timeLabel}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ClassCard({ cls }: { cls: Class }) {
  const lang = LANGUAGES.find((l) => l.value === cls.language);

  return (
    <Link href={`/classes/${cls.id}`}>
      <div className="group cursor-pointer" data-testid={`card-class-${cls.id}`}>
        <div className="relative overflow-hidden rounded-xl glass-card glass-card-hover">
          <img
            src={cls.thumbnailUrl || DEFAULT_THUMBNAIL}
            alt={cls.title}
            className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          {lang && (
            <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-white/80 glass-card" style={{ fontSize: "11px" }}>
              <Globe className="h-3 w-3" />
            </span>
          )}
        </div>
        <div className="mt-2.5">
          <h3 className="text-sm font-medium line-clamp-1" data-testid={`text-class-title-${cls.id}`}>
            {cls.title}
          </h3>
          {cls.category && (
            <Badge variant="secondary" className="mt-1 text-xs no-default-hover-elevate">
              {cls.category}
            </Badge>
          )}
          {cls.description && (
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
              {cls.description}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

function getFlagEmoji(code: string): string {
  const flags: Record<string, string> = {
    GB: "\u{1F1EC}\u{1F1E7}",
    ES: "\u{1F1EA}\u{1F1F8}",
    FR: "\u{1F1EB}\u{1F1F7}",
    DE: "\u{1F1E9}\u{1F1EA}",
    BR: "\u{1F1E7}\u{1F1F7}",
    IT: "\u{1F1EE}\u{1F1F9}",
    JP: "\u{1F1EF}\u{1F1F5}",
    KR: "\u{1F1F0}\u{1F1F7}",
    CN: "\u{1F1E8}\u{1F1F3}",
    SA: "\u{1F1F8}\u{1F1E6}",
    IN: "\u{1F1EE}\u{1F1F3}",
    UN: "\u{1F30D}",
  };
  return flags[code] || "\u{1F30D}";
}

export { ClassCard, getFlagEmoji };
